import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Database, Search, Gem, Crown, Shield, Target, Layers,
  BarChart3, TrendingUp, Dna, ArrowUp, Download, RefreshCw,
  Loader2, ChevronDown, ChevronUp, Filter, Zap, Activity,
  CheckCircle, XCircle, FlaskConical,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from '../config';

const REGIME_LABELS = {
  TREND_UP: { text: "text-emerald-400", bg: "bg-emerald-500/10", label: "Trend Up" },
  TREND_DOWN: { text: "text-red-400", bg: "bg-red-500/10", label: "Trend Down" },
  RANGE_LOW_VOL: { text: "text-blue-400", bg: "bg-blue-500/10", label: "Range LV" },
  RANGE_HIGH_VOL: { text: "text-orange-400", bg: "bg-orange-500/10", label: "Range HV" },
  BREAKOUT_EXPANSION: { text: "text-yellow-400", bg: "bg-yellow-500/10", label: "Breakout" },
  PANIC_LIQUIDATION: { text: "text-rose-400", bg: "bg-rose-500/10", label: "Panic" },
  MEAN_REVERTIVE_CHOP: { text: "text-violet-400", bg: "bg-violet-500/10", label: "Mean Rev" },
};

const TIER_COLORS = {
  EMERALD: "text-emerald-400",
  DIAMOND: "text-violet-400",
  GOLD: "text-amber-400",
  SILVER: "text-zinc-300",
  CANDIDATE: "text-zinc-500",
  LIVE_ELIGIBLE: "text-cyan-400",
};

const TIER_BG = {
  EMERALD: "bg-emerald-500/10 border-emerald-700/30",
  DIAMOND: "bg-violet-500/10 border-violet-700/30",
  GOLD: "bg-amber-500/10 border-amber-700/30",
  SILVER: "bg-zinc-500/10 border-zinc-700/30",
  CANDIDATE: "bg-zinc-800/50 border-zinc-700/30",
};

const GATE_ICONS = {
  basic_filter: Shield,
  regime_test: Target,
  out_of_sample: BarChart3,
  regime_oos: Layers,
  walk_forward: TrendingUp,
  monte_carlo: Dna,
};

/* ── Tier Stats Bar ── */
const TierStatsBar = ({ stats }) => {
  if (!stats) return null;
  const tiers = stats.tier_breakdown || {};
  const total = stats.total_strategies || 0;

  // Aggregate counts by tier (keys are like "GOLD_GOLD", "EMERALD_PAPER_TRACKING", etc.)
  const tierCounts = {};
  Object.entries(tiers).forEach(([key, data]) => {
    const tier = data?._id?.tier || key.split("_")[0];
    tierCounts[tier] = (tierCounts[tier] || 0) + (data?.count || 0);
  });

  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="tier-stats-bar">
      <div className="bg-zinc-800/60 border border-zinc-700/40 p-3">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</p>
        <p className="text-2xl font-bold font-mono text-zinc-100">{total}</p>
      </div>
      {["EMERALD", "DIAMOND", "GOLD", "SILVER", "CANDIDATE"].map(t => (
        <div key={t} className={`border p-3 ${TIER_BG[t] || "bg-zinc-800/50 border-zinc-700/30"}`}>
          <p className={`text-[10px] uppercase tracking-wider ${TIER_COLORS[t] || "text-zinc-500"}`}>{t}</p>
          <p className={`text-2xl font-bold font-mono ${TIER_COLORS[t] || "text-zinc-300"}`}>{tierCounts[t] || 0}</p>
        </div>
      ))}
    </div>
  );
};

/* ── Validation Pipeline Summary ── */
const ValidationSummary = ({ report, loading, onRun }) => (
  <Card className="bg-zinc-900/50 border-zinc-800" data-testid="validation-summary">
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" /> 5-Gate Validation Pipeline
          {report && (
            <span className="text-[10px] text-zinc-500 ml-2">
              {report.survivors || 0} survivors / {report.total_tested || 0} tested ({report.survival_rate || 0}%)
            </span>
          )}
        </h3>
        <Button size="sm" onClick={onRun} className="bg-red-700 hover:bg-red-600 text-white" data-testid="run-validation-btn">
          <Shield className="w-3 h-3 mr-1" /> Run Validation
        </Button>
      </div>

      {loading && <div className="flex items-center gap-2 text-zinc-500 text-xs py-3"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>}

      {!loading && !report && (
        <p className="text-zinc-600 text-xs text-center py-3">No validation results yet — run the pipeline above</p>
      )}

      {!loading && report && (
        <>
          <div className="flex items-center gap-4 mb-3 px-3 py-2 bg-zinc-800/30 text-[11px]">
            <span className="text-zinc-500">Tested: <span className="text-zinc-200 font-bold">{report.total_tested}</span></span>
            <span className="text-zinc-500">Survivors: <span className={`font-bold ${report.survivors > 0 ? "text-emerald-400" : "text-red-400"}`}>{report.survivors}</span></span>
            <span className="text-zinc-500">Rate: <span className={`font-bold ${report.survival_rate > 5 ? "text-emerald-400" : "text-amber-400"}`}>{report.survival_rate}%</span></span>
            <span className="text-zinc-500 ml-auto">Gate failures:</span>
            {Object.entries(report.gate_failures || {}).map(([gate, count]) => (
              <span key={gate} className="text-red-400 text-[10px]">{gate.replace(/_/g, " ")}: {count}</span>
            ))}
          </div>

          {report.passed_strategies?.length > 0 && (
            <div>
              <p className="text-[10px] text-emerald-400 font-bold uppercase mb-2">Survivors (Deployment-Ready)</p>
              {report.passed_strategies.map((s, i) => (
                <div key={i} className="border border-emerald-700/30 bg-emerald-950/10 p-3 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs text-emerald-300 font-bold">{s.strategy_name}</span>
                    <Badge className="text-[8px] bg-emerald-500/20 text-emerald-300">{s.asset}</Badge>
                    <Badge className="text-[8px] bg-cyan-500/20 text-cyan-300">{s.regime}</Badge>
                    <span className="text-[10px] text-zinc-500 ml-auto">Sharpe: {s.sharpe?.toFixed(2)} | R.Sharpe: {s.regime_sharpe?.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(s.last_validation?.gates || []).map((g, j) => {
                      const Icon = GATE_ICONS[g.gate] || Shield;
                      return (
                        <div key={j} className={`flex items-center gap-1 text-[9px] px-2 py-0.5 ${g.passed ? "bg-emerald-900/30 text-emerald-400" : "bg-red-900/30 text-red-400"}`}>
                          <Icon className="w-2.5 h-2.5" />
                          <span className="font-mono">{g.gate.replace(/_/g, " ")}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {report.survivors === 0 && (
            <p className="text-[10px] text-zinc-500 text-center py-2">
              No strategies survived all gates — the pipeline is filtering overfit edges.
            </p>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

/* ── Strategy Table ── */
const StrategyTable = ({ strategies, expanded, setExpanded }) => {
  if (!strategies?.length) {
    return <p className="text-center py-8 text-zinc-600 text-xs">Registry empty — run Import or a Mine first</p>;
  }

  return (
    <div className="overflow-x-auto" data-testid="strategy-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-zinc-500 border-b border-zinc-800 text-left">
            <th className="py-2.5 px-3">Strategy</th>
            <th className="py-2.5 px-3">Asset</th>
            <th className="py-2.5 px-3">Tier</th>
            <th className="py-2.5 px-3">Regime</th>
            <th className="py-2.5 px-3 text-right">Sharpe</th>
            <th className="py-2.5 px-3 text-right">R.Sharpe</th>
            <th className="py-2.5 px-3 text-right">APY%</th>
            <th className="py-2.5 px-3 text-right">Fitness</th>
            <th className="py-2.5 px-3 text-right">Gen</th>
            <th className="py-2.5 px-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {strategies.map((s, i) => {
            const tc = TIER_COLORS[s.tier] || "text-zinc-500";
            const rl = REGIME_LABELS[s.regime] || { text: "text-zinc-500", label: s.regime || "?" };
            const isExpanded = expanded === (s.strategy_id || i);

            return (
              <React.Fragment key={s.strategy_id || i}>
                <tr
                  onClick={() => setExpanded(isExpanded ? null : (s.strategy_id || i))}
                  className={`border-b border-zinc-800/30 cursor-pointer transition-colors hover:bg-zinc-800/40 ${
                    s.tier === "EMERALD" ? "bg-emerald-950/5" :
                    s.tier === "DIAMOND" ? "bg-violet-950/5" :
                    s.tier === "GOLD" ? "bg-amber-950/5" : ""
                  }`}
                  data-testid={`registry-row-${s.strategy_id || i}`}
                >
                  <td className="py-2 px-3 font-mono text-zinc-200 max-w-[180px] truncate">{s.strategy_name || s.strategy_id}</td>
                  <td className="py-2 px-3 text-zinc-400">{s.asset}</td>
                  <td className={`py-2 px-3 font-bold ${tc}`}>{s.tier}</td>
                  <td className={`py-2 px-3 font-mono ${rl.text}`}>{rl.label}</td>
                  <td className={`py-2 px-3 text-right font-mono ${s.sharpe >= 2.5 ? "text-violet-400" : s.sharpe >= 1.5 ? "text-amber-400" : "text-zinc-400"}`}>
                    {s.sharpe?.toFixed(2)}
                  </td>
                  <td className={`py-2 px-3 text-right font-mono ${(s.regime_sharpe || 0) >= 3 ? "text-emerald-400" : "text-zinc-500"}`}>
                    {s.regime_sharpe?.toFixed(2) || "—"}
                  </td>
                  <td className={`py-2 px-3 text-right font-mono ${(s.apy_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {s.apy_pct?.toFixed(1) || "—"}%
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-zinc-300">{s.fitness_score?.toFixed(1)}</td>
                  <td className="py-2 px-3 text-right font-mono text-zinc-500">{s.generation}</td>
                  <td className="py-2 px-3">
                    <Badge className={`text-[8px] ${
                      s.live_status === "VALIDATED" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                      s.live_status === "PAPER_TRACKING" ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/40" :
                      "bg-zinc-500/20 text-zinc-400 border-zinc-600"
                    }`}>{s.live_status || "—"}</Badge>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-zinc-900/80">
                    <td colSpan={10} className="px-4 py-3">
                      <div className="grid grid-cols-4 gap-3 text-[11px]">
                        <div><span className="text-zinc-500">Family:</span> <span className="text-zinc-300 font-mono">{s.family}</span></div>
                        <div><span className="text-zinc-500">Consistency:</span> <span className={`font-mono ${(s.consistency || 0) > 0 ? "text-emerald-400" : "text-red-400"}`}>{s.consistency?.toFixed(2)}</span></div>
                        <div><span className="text-zinc-500">Win Rate:</span> <span className="text-zinc-300 font-mono">{s.win_rate?.toFixed(1)}%</span></div>
                        <div><span className="text-zinc-500">Max DD:</span> <span className="text-zinc-300 font-mono">{s.max_dd?.toFixed(1)}%</span></div>
                        <div><span className="text-zinc-500">Trades:</span> <span className="text-zinc-300 font-mono">{s.total_trades}</span></div>
                        <div><span className="text-zinc-500">Source:</span> <span className="text-zinc-300 font-mono">{s.source || "mine"}</span></div>
                        <div><span className="text-zinc-500">Created:</span> <span className="text-zinc-300 font-mono">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</span></div>
                        <div><span className="text-zinc-500">Updated:</span> <span className="text-zinc-300 font-mono">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "—"}</span></div>
                      </div>
                      {s.params && (
                        <div className="mt-2 text-[10px] text-zinc-600">
                          <span className="text-zinc-500">Params:</span> {JSON.stringify(s.params).slice(0, 200)}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN STRATEGY REGISTRY PAGE
   ═══════════════════════════════════════════ */
export default function StrategyRegistryPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [strategies, setStrategies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [regimeFilter, setRegimeFilter] = useState("");
  const [assetFilter, setAssetFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const [validationReport, setValidationReport] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [survivorIds, setSurvivorIds] = useState([]);

  const fetchRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tierFilter) params.set("tier", tierFilter);
      if (regimeFilter) params.set("regime", regimeFilter);
      if (assetFilter) params.set("asset", assetFilter);
      params.set("limit", "200");

      const [strats, statsRes] = await Promise.all([
        axios.get(`${API}/regime/registry?${params}`, { headers }),
        axios.get(`${API}/regime/registry/stats`, { headers }),
      ]);
      setStrategies(strats.data?.strategies || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error("Registry fetch error:", e);
    }
    setLoading(false);
  }, [tierFilter, regimeFilter, assetFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchValidation = useCallback(async () => {
    setValidationLoading(true);
    try {
      const r = await axios.get(`${API}/regime/validation-report?limit=50`, { headers });
      setValidationReport(r.data);
      const passed = r.data?.passed_strategies || [];
      setSurvivorIds(passed.map(s => `${s.strategy_name || ""}_${s.asset || "ETHUSDT"}_4h_gen0`));
    } catch {}
    setValidationLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRegistry(); fetchValidation(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRegistry(); }, [tierFilter, regimeFilter, assetFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkImport = async () => {
    try {
      const r = await axios.post(`${API}/regime/registry/bulk-import`, {}, { headers });
      toast.success(`Imported ${r.data?.total || 0} strategies`);
      fetchRegistry();
    } catch { toast.error("Import failed"); }
  };

  const evolve = async () => {
    try {
      const r = await axios.post(`${API}/regime/evolve`, {}, { headers });
      toast.success(`Evolution: ${r.data?.improvements || 0} improvements from ${r.data?.offspring_tested || 0} tested`);
      fetchRegistry();
    } catch { toast.error("Evolution failed"); }
  };

  const promote = async () => {
    try {
      const r = await axios.post(`${API}/regime/promote`, {}, { headers });
      toast.success(`${r.data?.total_promotions || 0} promoted, ${r.data?.total_demotions || 0} demoted`);
      fetchRegistry();
    } catch { toast.error("Promotion failed"); }
  };

  const runValidation = async () => {
    try {
      await axios.post(`${API}/regime/validate-batch?tier_target=EMERALD&limit=50`, {}, { headers });
      toast.success("5-gate validation launched — results in 2-3 minutes");
      setTimeout(fetchValidation, 120000);
    } catch { toast.error("Validation failed"); }
  };

  // Client-side search filter
  const filtered = search
    ? strategies.filter(s =>
        (s.strategy_name || s.strategy_id || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.asset || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.family || "").toLowerCase().includes(search.toLowerCase())
      )
    : strategies;

  return (
    <div className="space-y-5" data-testid="strategy-registry-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Database className="w-6 h-6 text-violet-400" />
            Strategy Registry
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Your library of validated trading edges
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={bulkImport} className="border-zinc-700 text-zinc-400" data-testid="bulk-import-btn">
            <Download className="w-3 h-3 mr-1" /> Import
          </Button>
          <Button size="sm" variant="outline" onClick={evolve} className="border-cyan-700 text-cyan-400" data-testid="evolve-btn">
            <Dna className="w-3 h-3 mr-1" /> Evolve
          </Button>
          <Button size="sm" variant="outline" onClick={promote} className="border-violet-700 text-violet-400" data-testid="promote-btn">
            <ArrowUp className="w-3 h-3 mr-1" /> Promote
          </Button>
          <Button size="sm" variant="outline" onClick={fetchRegistry} className="border-zinc-700 text-zinc-400" data-testid="refresh-registry-btn">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Tier Stats */}
      <TierStatsBar stats={stats} />

      {/* Survivor Cards (validated strategies in paper tracking) */}
      {survivorIds.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-bold text-emerald-400 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Validated Survivors ({survivorIds.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {survivorIds.map(id => (
              <Card key={id} className="bg-emerald-950/20 border-emerald-800/30">
                <CardContent className="p-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-sm font-mono text-emerald-300 truncate">{id}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Validation Pipeline */}
      <ValidationSummary report={validationReport} loading={validationLoading} onRun={runValidation} />

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search strategies..."
                className="pl-8 bg-zinc-900 border-zinc-700 text-zinc-200 text-sm h-9"
                data-testid="search-registry"
              />
            </div>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm h-9"
              data-testid="filter-tier"
            >
              <option value="">All Tiers</option>
              <option value="EMERALD">EMERALD</option>
              <option value="DIAMOND">DIAMOND</option>
              <option value="GOLD">GOLD</option>
              <option value="SILVER">SILVER</option>
              <option value="CANDIDATE">CANDIDATE</option>
            </select>
            <select
              value={regimeFilter}
              onChange={(e) => setRegimeFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm h-9"
              data-testid="filter-regime"
            >
              <option value="">All Regimes</option>
              {Object.entries(REGIME_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 text-zinc-200 px-3 py-1.5 text-sm h-9"
              data-testid="filter-asset"
            >
              <option value="">All Assets</option>
              <option value="ETHUSDT">ETHUSDT</option>
              <option value="BTCUSDT">BTCUSDT</option>
              <option value="SOLUSDT">SOLUSDT</option>
              <option value="BNBUSDT">BNBUSDT</option>
            </select>
            <div className="text-xs text-zinc-500 self-center whitespace-nowrap">
              {filtered.length} strategies
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategy Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading registry...
            </div>
          ) : (
            <StrategyTable strategies={filtered} expanded={expanded} setExpanded={setExpanded} />
          )}
        </CardContent>
      </Card>

      <div className="text-[10px] text-zinc-600 px-1">
        R.Sharpe = Regime-specific Sharpe | APY% = Annualized Return | Gen = Evolution generation | Click row to expand
      </div>
    </div>
  );
}
